import React, {
  useContext, useEffect, useMemo, useRef, useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Cart, Image, Link, Divider, Typography, Tooltip
} from '@telus/koodo-ui-kit'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { PLANS_STATE } from '@apps/redux/features/PlansSlice'
import { PHONE_NUMBER_STATE } from '@apps/redux/features/PhoneNumberSlice'
import {
  PAYMENT_STATE,
  updatePaymentStates,
  ccInfo,
  paymentVoucher,
  paymentSummary,
  promoCode as promoCodeInitState
} from '@apps/redux/features/PayAndReviewSlice'
import { ACCOUNT_SETUP_STATE } from '@apps/redux/features/AccountSetupSlice'
import ProxyOperations from '@apps/flows/activation/services/operations'
import { getTaxDetails, getTaxProvince, getTaxProvinceId } from '@apps/utils/common'
import { fetchServiceFee911 } from '@apps/utils/serviceFee911'
import { fetchTax } from '@apps/utils/taxLookup'
import { resolveBundleName } from '@apps/utils/promoBundle'
import { PAYMENT_OPTIONS, SIM_OPTIONS } from '@apps/utils/constants'
import { getById, getBySlug, getImgByTitle } from '@apps/utils/contentful'
import { currencyFormatter } from '@apps/utils/CurrencyFormatter'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'
import Sites from '@sites/index'
import useLocale from '../../../_engine/hooks/useLocale'

const toSafeText = (value) => (typeof value === 'string' ? value : '')

// Order summary intentionally hides footnote markers, so literal
// <sup>N</sup> HTML strings and unicode superscript chars (⁰-⁹) are
// stripped along with the commas that only join them (e.g. "speed⁴,⁵").
const FEATURE_SUPERSCRIPT = '<sup>[\\s\\S]*?<\\/sup>|[⁰¹²³⁴⁵⁶⁷⁸⁹]'
const FEATURE_SUPERSCRIPT_REGEX = new RegExp(`(?:${FEATURE_SUPERSCRIPT})(?:\\s*,\\s*(?:${FEATURE_SUPERSCRIPT}))*`, 'g')

// Converts raw plan feature strings into React nodes so both authoring
// styles for bold render without dangerouslySetInnerHTML: {{text}} wrappers
// and **text** markdown. The Plan card gets markdown for free because the
// kit's ContentRenderer resolves it; this list builds its own nodes.
const FEATURE_TOKEN_REGEX = /\{\{([\s\S]*?)\}\}|\*\*([\s\S]+?)\*\*/g

// A {{...}} wrapper may itself be authored as {{*text*}} or {{**text**}};
// those markers are syntax, not part of the label. Mirrors the Plan card's
// normalizeWrappedBoldText.
const WRAPPED_BOLD_MARKER_REGEX = /^\*{1,2}([\s\S]+?)\*{1,2}$/

const normalizeBoldText = (value) => {
  const trimmed = value.trim()
  const [, unwrapped] = WRAPPED_BOLD_MARKER_REGEX.exec(trimmed) || []
  return unwrapped ? unwrapped.trim() : trimmed
}

const renderFeatureText = (feature) => {
  if (typeof feature !== 'string') return feature
  const text = feature.replace(FEATURE_SUPERSCRIPT_REGEX, '')
  const nodes = []
  let lastIndex = 0
  let keyIndex = 0
  FEATURE_TOKEN_REGEX.lastIndex = 0
  let match = FEATURE_TOKEN_REGEX.exec(text)
  while (match !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }
    const boldText = match[1] !== undefined ? normalizeBoldText(match[1]) : match[2].trim()
    // eslint-disable-next-line no-plusplus
    nodes.push(<strong key={keyIndex++}>{boldText}</strong>)
    lastIndex = FEATURE_TOKEN_REGEX.lastIndex
    match = FEATURE_TOKEN_REGEX.exec(text)
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }
  return nodes
}

/*
  Splits a voucher-paid order into what is still due today and what is credited
  to the account as available funds. The 911 fee is part of what the voucher has
  to cover, so it belongs on the charge side of this subtraction.

  One function rather than the calculation inlined per call site: the payment
  page and the transaction summary both render PAYMENT_STATE.SUMMARY.AMOUNT_FUNDS
  straight out of redux, so any two call sites that disagree about whether the
  fee counts show the customer two different numbers for the same order.
*/
const splitVoucherBalance = (subTotal, serviceFee, totalVoucherAmount) => {
  const baseDue = Number(subTotal) + Number(serviceFee || 0) - Number(totalVoucherAmount)

  return baseDue > 0
    ? {
      [PAYMENT_STATE.SUMMARY.AMOUNT_DUE]: baseDue.toFixed(2),
      [PAYMENT_STATE.SUMMARY.AMOUNT_FUNDS]: (0.0).toFixed(2)
    }
    : {
      [PAYMENT_STATE.SUMMARY.AMOUNT_DUE]: (0.0).toFixed(2),
      [PAYMENT_STATE.SUMMARY.AMOUNT_FUNDS]: Math.abs(baseDue).toFixed(2)
    }
}

const OrderSummary = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { prov } = useLocale() || {}
  const { getTax, get911ServiceFees } = ProxyOperations('public')

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.PROVINCES]: mappedProvinceList
  } = useContext(ApplicationContext)

  const {
    userType,
    content: { pageContent, featureFlags: pageFeatureFlags }
  } = useSelector((state) => state.common)
  const {
    [PLANS_STATE.PLAN]: selectedPlan,
    [PLANS_STATE.PROMO]: planPromo,
    [PLANS_STATE.PRE_SELECTED_PLAN]: preSelectedPlan
  } = useSelector((state) => state.plansPage)

  const featureFlags = pageFeatureFlags?.[0] || {}
  const isServiceFee911Enabled = featureFlags?.isServiceFee911Enabled
  const enableMultiPromo = featureFlags?.enableMultiPromo

  const paymentState = useSelector((state) => state.payAndReviewPage) || {}
  const {
    [PAYMENT_STATE.VOUCHER.ADDED_VOUCHERS]: addedVouchers,
    [PAYMENT_STATE.SELECTED_PAYMENT_OPTION]: selectedPaymentOption,
    [PAYMENT_STATE.PROMO.PROMOTION_CODE_INFO]: promotionCodeInfo,
    [PAYMENT_STATE.PROMO.MULTI_PROMOTION_CODE_INFO]: multiPromotionCodeInfo,
    [PAYMENT_STATE.PROMO.MULTI_PROMO_CODES]: multiPromoCodes,
    [PAYMENT_STATE.SUMMARY.AMOUNT_OWING]: amountOwing,
    [PAYMENT_STATE.SUMMARY.AMOUNT_DUE]: amountDue,
    [PAYMENT_STATE.SUMMARY.AMOUNT_FUNDS]: amountFunds,
    [PAYMENT_STATE.SUMMARY.AMOUNT_TOTAL_VOUCHER]: amountTotalVoucher,
    [PAYMENT_STATE.SUMMARY.TAX_INFO]: taxInfo,
    [PAYMENT_STATE.SUMMARY.SERVICE_FEE_911]: serviceFee911,
    [PAYMENT_STATE.SUMMARY.SERVICE_FEE_NAME]: serviceFeeName,
    [PAYMENT_STATE.SUMMARY.ESTIMATED_RECURRING_CHARGE]: estimatedRecurringCharge,
    [PAYMENT_STATE.SUMMARY.MONTHLY_TAX_INFO]: monthlyTaxInfo,
    [PAYMENT_STATE.SUMMARY.MONTHLY_TAX]: monthlyTax,
    // Vouchers are tax free, so the voucher path never calls getTax — and the
    // tax province is only ever produced as a by-product of that call. These
    // hold the value from before the switch so it can be carried forward
    // instead of being wiped by the payment-option reset.
    [PAYMENT_STATE.PROMO.MULTI_PROMOTION_CODE_LOADER]: isPromoApplying,
    [PAYMENT_STATE.SUMMARY.TAX_PROVINCE]: storedTaxProvince,
    [PAYMENT_STATE.SUMMARY.TAX_PROVINCE_ID]: storedTaxProvinceId
  } = paymentState

  const phoneNumberState = useSelector((state) => state.phoneNumberPage) || {}
  const {
    [PHONE_NUMBER_STATE.SELECTED_PHONE_NUMBER]: selectedPhoneNumber,
    [PHONE_NUMBER_STATE.SELECTED_PHONE_NUMBER_OPTION]: selectedPhoneNumberOption,
    [PHONE_NUMBER_STATE.PROVINCE_NAME]: provinceName
  } = phoneNumberState

  const accountSetupPage = useSelector((state) => state.accountSetupPage) || {}
  const { [ACCOUNT_SETUP_STATE.PROVINCE]: provinceInfo } = accountSetupPage

  const simSelectionState = useSelector((state) => state.simSelectionPage) || {}
  const { simType: simSelection, cost: simSelectionCost } = simSelectionState.selectSimType || {}

  const isConsumer = userType === Sites.consumer

  // Render the kit skeleton while Contentful copy / Redux plan data are still
  // resolving. Same shell + toggle as the populated Cart, with placeholder
  // bars in place of the items zone and totals. Mirrors the loading pattern
  // used in PaymentSummary (useState + flip when prerequisites land).
  const [isLoading, setIsLoading] = useState(true)
  // Held true while the 911 fee + tax chain is in flight so the rendered
  // totals never disagree with the redux store mid-fetch.
  const [isLoadingFees, setIsLoadingFees] = useState(false)

  useEffect(() => {
    if (pageContent && language && userType) {
      setIsLoading(false)
    }
  }, [pageContent, language, userType])

  // Re-resolving the selected plan for the active locale is owned by
  // ActivationRoutes: it holds the getPlanById fallback for when the
  // `localizedPlanItem` catalog is not hydrated (which is the usual case since
  // MASC-17185), and being the single flow-level mount it cannot fetch once per
  // page the way this component would.

  const text = useMemo(() => {
    if (!pageContent || !language || !userType) return {}
    const commonElements = getById(pageContent, 'commonElements')?.[language]
    const commonShort = commonElements?.shortTextFields
    const paymentPageSuffix = userType === Sites.consumer ? 'Consumer' : 'Dealer'
    const paymentPage = getById(pageContent, `paymentPage${paymentPageSuffix}`)?.[language]
    const paymentShort = paymentPage?.shortTextFields
    return {
      orderSummaryTitle: getBySlug(commonShort, 'orderSummaryTitle')?.value,
      showTitle: getBySlug(commonShort, 'showTitle')?.value,
      hideTitle: getBySlug(commonShort, 'hideTitle')?.value,
      firstPaymentTitle: getBySlug(commonShort, 'firstPaymentTitle')?.value,
      monthlyPaymentTitle: getBySlug(commonShort, 'monthlyPaymentTitle')?.value,
      subTotalTitle: getBySlug(commonShort, 'subTotalTitle')?.value,
      dueTodayTitle: getBySlug(commonShort, 'dueTodayTitle')?.value,
      dueMonthlyTitle: getBySlug(commonShort, 'dueMonthlyTitle')?.value,
      changePlanLinkTitle: getBySlug(commonShort, 'changePlanLinkTitle')?.value,
      noPlanSelectedTitle: getBySlug(commonShort, 'noPlanSelectedTitle')?.value,
      itemsTitle: getBySlug(commonShort, 'itemsTitle')?.value,
      oneTimePaymentTitle:
        getBySlug(commonShort, 'oneTimePaymentTitle')?.value
        || getBySlug(commonShort, 'oneTimePaymentLabel')?.value
        || getBySlug(paymentShort, 'oneTimePaymentTitle')?.value
        || getBySlug(paymentShort, 'oneTimePaymentLabel')?.value,
      serviceFee911Attempt: getBySlug(commonShort, 'get-serviceFee911-attempt')?.value,
      vouchersTitle: getBySlug(commonShort, 'vouchersTitle')?.value,
      estimatedTaxLabel: getBySlug(commonShort, 'estimatedTaxLabel')?.value,
      estimatedTaxTooltip: getBySlug(commonShort, 'estimatedTaxTooltip')?.value,
      serviceFeeCollected: getBySlug(commonShort, 'serviceFeeCollected')?.value,
      voucherExcess: getBySlug(commonShort, 'voucherExcess')?.value,
      fundsAddedToTheAccount: getBySlug(commonShort, 'fundsAddedToTheAccount')?.value,
      planLogoUrl: getImgByTitle(commonElements, 'planLogo')?.url,
      planLogoAlt: getImgByTitle(commonElements, 'planLogo')?.altText || ''
    }
  }, [pageContent, language, userType])

  const phoneNumberSelected = () => (
    selectedPhoneNumber
    || phoneNumberState?.[PHONE_NUMBER_STATE.CREATE_NEW_NUMBER.PHONE_NUMBER]?.id
    || (
      typeof phoneNumberState?.[PHONE_NUMBER_STATE.CREATE_NEW_NUMBER.PHONE_NUMBER] === 'string'
      && phoneNumberState?.[PHONE_NUMBER_STATE.CREATE_NEW_NUMBER.PHONE_NUMBER]
    )
  )

  // Promo priority: manual > multi-promo (if flagged) > plan promo.
  // No stacking — same rules as PaymentSummary.
  const getPromoBundle = () => {
    if (promotionCodeInfo) return promotionCodeInfo
    if (enableMultiPromo && multiPromotionCodeInfo?.length > 0) return multiPromotionCodeInfo
    if (planPromo) return planPromo
    return null
  }

  const calculateSubTotal = (plnData, promoCodes, simCost, serviceFee) => {
    if ((plnData && plnData.value) || simCost) {
      let result = Number(plnData?.value ?? 0)

      if (promoCodes && promoCodes.bundles) {
        promoCodes.bundles.forEach((bundle) => {
          if (bundle.fee && !Number.isNaN(bundle.fee) && Math.abs(bundle.fee) !== 0) {
            result += Number(bundle.fee)
          }
        })
      } else if (enableMultiPromo && promoCodes) {
        promoCodes.forEach((promoCode) => {
          if (promoCode.bundles) {
            promoCode.bundles.forEach((bundle) => {
              if (bundle.fee && !Number.isNaN(bundle.fee) && Math.abs(bundle.fee) !== 0) {
                result += Number(bundle.fee)
              }
            })
          }
        })
      }

      if (simCost) result += Number(simCost)
      if (serviceFee) result += Number(serviceFee)

      return result.toFixed(2)
    }
    return (0.0).toFixed(2)
  }

  const getServiceFeeInfo = (serviceFeesResponse, lang) => {
    const info = { serviceFee: null, serviceName: null }
    if (!Array.isArray(serviceFeesResponse) || serviceFeesResponse.length === 0) return info
    if (typeof serviceFeesResponse[0]?.fee === 'number') {
      info.serviceFee = Number(serviceFeesResponse[0].fee) / 100
    }
    if (
      serviceFeesResponse[0]?.serviceName
      && typeof serviceFeesResponse[0].serviceName === 'object'
    ) {
      info.serviceName = serviceFeesResponse[0].serviceName[lang]
    }
    return info
  }

  // eslint-disable-next-line no-async-promise-executor, no-promise-executor-return
  const get911ServiceFeesAndRetry = async (planId, phoneNum, maxRetries, lang) => (
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve) => {
      // Coerce maxRetries to a positive integer — if Contentful hasn't
      // populated `get-serviceFee911-attempt` the while loop would never
      // enter and the Promise would hang, leaving the sidebar in the
      // loading state forever.
      const attempts = Number(maxRetries) > 0 ? Number(maxRetries) : 1
      let retries = 0
      while (retries < attempts) {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetchServiceFee911(get911ServiceFees, planId, phoneNum).catch(() => null)
        const { serviceFee } = getServiceFeeInfo(res, lang)
        if (serviceFee !== null || retries === attempts - 1) {
          resolve(res)
          return
        }
        retries += 1
      }
      resolve(null)
    })
  )

  // Track previous plan / province / payment-option across renders to know
  // when to clear stale promo / voucher / cc state. PaymentSummary used a
  // module-level `let prevOption` for this — refs scope it to the instance
  // so two mounted summaries can't trample each other.
  const prevPlanIdRef = useRef(selectedPlan?.id)
  const prevProvinceRef = useRef(provinceInfo)
  const prevOptionRef = useRef('')

  // The 911 fee is fetched on its own, keyed on the only two inputs the API
  // takes: get911ServiceFees(pricePlan, phoneNumber). It used to be fetched
  // inside the summary effect, which re-runs on any of fifteen dependencies, so
  // a promo, a voucher, a payment-option switch or a route change re-requested a
  // fee that none of them can change. `language` is not a key either: the
  // response carries serviceName in both locales and getServiceFeeInfo picks one.
  const [serviceFeeResult, setServiceFeeResult] = useState({ key: null, response: null })

  const serviceFeeKey = isServiceFee911Enabled && selectedPlan?.id && selectedPhoneNumber
    ? `${selectedPlan.id}|${selectedPhoneNumber}`
    : null

  useEffect(() => {
    if (!serviceFeeKey || serviceFeeResult.key === serviceFeeKey) return undefined

    let cancelled = false
    setIsLoadingFees(true)
    get911ServiceFeesAndRetry(
      selectedPlan?.id,
      selectedPhoneNumber,
      text?.serviceFee911Attempt,
      language
    ).catch(() => null).then((response) => {
      if (cancelled) return
      setServiceFeeResult({ key: serviceFeeKey, response })
    })

    return () => {
      cancelled = true
    }
    // serviceFeeKey is the only dependency on purpose. get911ServiceFeesAndRetry
    // is recreated each render, but everything it closes over that can change the
    // result — the plan id and phone number — is already in the key, so a changed
    // value produces a new key and a fresh run. setIsLoadingFees is a useState
    // setter, which React keeps stable across renders. Listing either would make
    // this refetch on unrelated renders, which is what the split was for.
  }, [serviceFeeKey])

  useEffect(() => {
    const subTotal = calculateSubTotal(selectedPlan, getPromoBundle(), simSelectionCost)

    let updatePaymentState = {}

    if (prevProvinceRef.current !== provinceInfo || prevPlanIdRef.current !== selectedPlan?.id) {
      updatePaymentState = { ...updatePaymentState, ...promoCodeInitState }
    }

    if (selectedPaymentOption !== prevOptionRef.current && prevOptionRef.current !== '') {
      updatePaymentState = {
        ...paymentVoucher,
        ...paymentSummary,
        ...ccInfo,
        [PAYMENT_STATE.TERMS_OF_SERVICE]: false,
        [PAYMENT_STATE.AUTO_TOP_UPS]: true,
        [PAYMENT_STATE.IS_COMPLETE]: false,
        // paymentSummary blanks the tax province along with the amounts, but a
        // province is not a stale amount — it describes the customer's number.
        // The voucher path never calls getTax, so once it is blanked nothing
        // re-derives it, and a port-in has no phoneProvinceId to fall back on:
        // promo codes are then rejected with "Province is required". Kept here
        // rather than only in the voucher branch, so no later dispatch of
        // updatePaymentState can blank it again.
        [PAYMENT_STATE.SUMMARY.TAX_PROVINCE]: storedTaxProvince,
        [PAYMENT_STATE.SUMMARY.TAX_PROVINCE_ID]: storedTaxProvinceId
      }
    }
    prevOptionRef.current = selectedPaymentOption
    prevPlanIdRef.current = selectedPlan?.id
    prevProvinceRef.current = provinceInfo

    updatePaymentState = {
      ...updatePaymentState,
      [PAYMENT_STATE.SUMMARY.AMOUNT_OWING]: subTotal,
      // Formatted, not a bare 0: this object is dispatched at the end of the
      // effect, after the branches above, so its value is the one that survives.
      // getActivateAllReqPayload passes amountTax straight into the activateAll
      // mutation, which rejects a number where it expects "0.00" — every voucher
      // activation failed with a 500 while the card path, which always computes
      // a formatted amount, was unaffected.
      [PAYMENT_STATE.SUMMARY.AMOUNT_TAX]: (0.0).toFixed(2),
      // serviceFee911 stays a number on purpose, unlike the tax above. It is
      // formatted at the payload boundary rather than passed through raw, so its
      // type never reaches the mutation — and it is read as a truthiness check
      // below, where "0.00" would be truthy and 0 is not.
      [PAYMENT_STATE.SUMMARY.SERVICE_FEE_911]: 0,
      [PAYMENT_STATE.SUMMARY.SERVICE_FEE_NAME]: ''
    }

    const totalVoucherAmount = (addedVouchers || []).reduce((prevTotal, curr) => (
      (Number(prevTotal) || 0) + (Number(curr.totalAmount) || 0)
    ), 0)

    if (selectedPaymentOption === PAYMENT_OPTIONS.VOUCHER) {
      updatePaymentState[PAYMENT_STATE.SUMMARY.AMOUNT_TOTAL_VOUCHER] = totalVoucherAmount.toFixed(2)
      // Provisional: serviceFee911 here is whatever is in redux right now, which
      // is 0 until the fee lands. When the 911 flag is on, the branch that owns
      // the dispatch recomputes this from the resolved fee before it ships.
      updatePaymentState = {
        ...updatePaymentState,
        ...splitVoucherBalance(subTotal, serviceFee911, totalVoucherAmount)
      }
    }

    // Hold the tax lookup while a promo is being applied. The ATU promo lands on
    // the payment page about a second after mount, so taxing before it arrives
    // charged tax on a subtotal that was about to change — one wasted request per
    // load, and a figure briefly shown to the user that was not the one they pay.
    // MultiPromoCode raises this flag for the duration and clears it on every
    // exit path, so the effect re-runs with the settled cart.
    // The skeleton is the only signal that the cart is recalculating, so raise it
    // for the whole window in which the totals are unsettled — including the wait
    // above while a promo is applied. Cleared by each branch once it dispatches.
    if (hasPlan && phoneNumberSelected()) {
      setIsLoadingFees(true)
    } else {
      // Nothing to look up without a plan and a number, so lower it rather than
      // leaving the skeleton up if either is cleared while this stays mounted.
      setIsLoadingFees(false)
    }

    if (hasPlan && phoneNumberSelected() && !isPromoApplying) {
      if (isServiceFee911Enabled) {
        // When the 911 flag is on, getTax is called with '' for the
        // province so the service infers it from the phone number — that's
        // the contract PaymentSummary established and the tax backend
        // depends on.
        // Fee comes from the effect above, keyed on plan and phone number.
        // Nothing to do until it has resolved for the current key.
        if (serviceFeeResult.key !== serviceFeeKey) return
        const runWithServiceFee = (serviceFeesResponse) => {
          const { serviceFee, serviceName } = getServiceFeeInfo(serviceFeesResponse, language)
          let augmentedSubTotal = subTotal
          if (serviceFee) {
            augmentedSubTotal = (Number(subTotal) + serviceFee).toFixed(2)
          }
          const recurring = calculateSubTotal(selectedPlan, null, 0, serviceFee)
          if (selectedPaymentOption === PAYMENT_OPTIONS.VOUCHER) {
            dispatch(updatePaymentStates({
              ...updatePaymentState,
              // Recomputed from the fee this branch resolved, not the one that
              // was in redux when the effect started. The staged value above is
              // calculated before the fee settles, so on the run that first
              // learns the fee it is short by exactly the fee - the customer saw
              // the right excess on the payment page and a larger one on the
              // transaction summary, which reads this same value back out of
              // redux without recomputing (MASC-17475).
              ...splitVoucherBalance(subTotal, serviceFee, totalVoucherAmount),
              [PAYMENT_STATE.SUMMARY.AMOUNT_OWING]: augmentedSubTotal,
              [PAYMENT_STATE.SUMMARY.AMOUNT_TAX]: (0.0).toFixed(2),
              // Coalesced because this branch now owns the dispatch. getServiceFeeInfo
              // reports null for a failed or empty lookup — it is what tells the retry
              // helper to try again — and the base dispatch used to land afterwards and
              // turn that into the reset 0 / ''. Left as null, getActivateAllReqPayload
              // drops serviceFee911 from amountInfo entirely (it guards on !== null) and
              // the activation is rejected with "Service Fee 911 is mandatory", where a
              // failed lookup previously sent "0.00".
              [PAYMENT_STATE.SUMMARY.SERVICE_FEE_911]: serviceFee ?? 0,
              [PAYMENT_STATE.SUMMARY.SERVICE_FEE_NAME]: serviceName ?? '',
              [PAYMENT_STATE.SUMMARY.ESTIMATED_RECURRING_CHARGE]: recurring,
              // Carried forward from the card path, which is always where the
              // flow starts. Without it a port-in customer has no province at
              // all on voucher — the phone number was never picked from our
              // pool — and promo validation is rejected for a missing province.
              [PAYMENT_STATE.SUMMARY.TAX_PROVINCE]: storedTaxProvince,
              [PAYMENT_STATE.SUMMARY.TAX_PROVINCE_ID]: storedTaxProvinceId
            }))
            setIsLoadingFees(false)
            // Claims the dispatch so the caller skips the base one at the end of
            // the effect. This branch is synchronous, so that dispatch lands after
            // it and reverts every field overridden above: serviceFee911 to 0,
            // serviceFeeName to '', amountOwing to a subtotal without the fee and
            // estimatedRecurringCharge to paymentSummary's '0.00'. Nothing is lost
            // by skipping it — the object above already spreads it. Rescuing one
            // field at a time is what made this three regressions (#2816
            // taxProvince, #2820 taxAmount, then the fee), so the order is fixed
            // instead. The card path keeps the existing ordering: it dispatches
            // from a promise callback, which lands after the base dispatch anyway.
            return true
          }

          // Monthly tax is recomputed on the recurring base — plan value + 911
          // fee (no one-time SIM cost, no first-payment promo) — so the Monthly
          // payment section reflects the true ongoing charge. `recurring` already
          // folds in the 911 fee. Same province contract as the first-payment
          // call ('' lets the backend infer it from the phone).
          const monthlyBase = recurring
          // Reuse the first-payment tax response when the monthly base matches,
          // so we don't fire a second identical request.
          // Both amounts are requested; fetchTax collapses identical keys and
          // serves anything already looked up, including across a remount.
          Promise.all([
            fetchTax(getTax, augmentedSubTotal, '', selectedPhoneNumber),
            fetchTax(getTax, monthlyBase, '', selectedPhoneNumber)
          ]).then(([responseTax, fetchedMonthly]) => {
            const monthlyResponseTax = fetchedMonthly || responseTax
            const taxDetails = getTaxDetails(responseTax, language)
            const taxProvince = getTaxProvince(responseTax)?.[0]
            const taxProvinceId = getTaxProvinceId(mappedProvinceList, taxProvince)
            const taxVal = taxDetails
              ?.map((c) => c.value)
              .reduce((p, c) => Number(p) + Number(c), 0)
            const taxCalc = taxVal?.toFixed(2) || (0.0).toFixed(2)
            const total = (Number(augmentedSubTotal) + Number(taxCalc)).toFixed(2)
            const monthlyTaxDetails = getTaxDetails(monthlyResponseTax, language)
            const monthlyTaxVal = monthlyTaxDetails
              ?.map((c) => c.value)
              .reduce((p, c) => Number(p) + Number(c), 0)
            const monthlyTaxCalc = monthlyTaxVal?.toFixed(2) || (0.0).toFixed(2)
            dispatch(updatePaymentStates({
              ...updatePaymentState,
              [PAYMENT_STATE.SUMMARY.AMOUNT_OWING]: total,
              [PAYMENT_STATE.SUMMARY.AMOUNT_TAX]: taxCalc,
              [PAYMENT_STATE.SUMMARY.TAX_INFO]: taxDetails,
              [PAYMENT_STATE.SUMMARY.TAX_PROVINCE]: taxProvince,
              [PAYMENT_STATE.SUMMARY.TAX_PROVINCE_ID]: taxProvinceId,
              [PAYMENT_STATE.SUMMARY.SERVICE_FEE_911]: serviceFee ?? 0,
              [PAYMENT_STATE.SUMMARY.SERVICE_FEE_NAME]: serviceName ?? '',
              [PAYMENT_STATE.SUMMARY.ESTIMATED_RECURRING_CHARGE]: recurring,
              [PAYMENT_STATE.SUMMARY.MONTHLY_TAX_INFO]: monthlyTaxDetails,
              [PAYMENT_STATE.SUMMARY.MONTHLY_TAX]: monthlyTaxCalc
            }))
            setIsLoadingFees(false)
          }).catch(() => {
            // Tax lookup failed: clear the loader and fall back to the amount
            // owing without tax so the summary renders instead of hanging.
            dispatch(updatePaymentStates({
              ...updatePaymentState,
              [PAYMENT_STATE.SUMMARY.AMOUNT_OWING]: augmentedSubTotal,
              [PAYMENT_STATE.SUMMARY.AMOUNT_TAX]: (0.0).toFixed(2),
              [PAYMENT_STATE.SUMMARY.SERVICE_FEE_911]: serviceFee ?? 0,
              [PAYMENT_STATE.SUMMARY.SERVICE_FEE_NAME]: serviceName ?? '',
              [PAYMENT_STATE.SUMMARY.ESTIMATED_RECURRING_CHARGE]: recurring
            }))
            setIsLoadingFees(false)
          })
          return false
        }

        // Truthy only from the voucher branch, which dispatches synchronously and
        // so must not be followed by the base dispatch at the end of the effect.
        if (runWithServiceFee(serviceFeeResult.response)) return
      } else if (selectedPaymentOption !== PAYMENT_OPTIONS.VOUCHER) {
        // 'NL' is normalized to 'NF' because the tax backend uses the
        // legacy "Newfoundland" code.
        const province = provinceName || prov
        const selectedProv = selectedPhoneNumberOption?.toUpperCase() === 'TRANSFER_NUMBER'
          ? prov
          : province
        const correctedProvince = selectedProv?.toUpperCase() === 'NL'
          ? 'NF'
          : selectedProv?.toUpperCase()
        // Recurring base is the plan value (911 disabled → no fee), recomputed
        // here so the Monthly payment section's tax and Due monthly are accurate.
        const monthlyBase = Number(selectedPlan?.value ?? 0).toFixed(2)
        // Reuse the first-payment tax response when the monthly base matches,
        // so we don't fire a second identical request.
        Promise.all([
          fetchTax(getTax, subTotal, correctedProvince, selectedPhoneNumber),
          fetchTax(getTax, monthlyBase, correctedProvince, selectedPhoneNumber)
        ]).then(([responseTax, fetchedMonthly]) => {
          const monthlyResponseTax = fetchedMonthly || responseTax
          const taxDetails = getTaxDetails(responseTax, language)
          const taxProvince = getTaxProvince(responseTax)?.[0]
          const taxProvinceId = getTaxProvinceId(mappedProvinceList, taxProvince)
          const taxVal = taxDetails
            ?.map((c) => c.value)
            .reduce((p, c) => Number(p) + Number(c), 0)
          const taxCalc = taxVal?.toFixed(2) || (0.0).toFixed(2)
          const total = (Number(subTotal) + Number(taxCalc)).toFixed(2)
          const monthlyTaxDetails = getTaxDetails(monthlyResponseTax, language)
          const monthlyTaxVal = monthlyTaxDetails
            ?.map((c) => c.value)
            .reduce((p, c) => Number(p) + Number(c), 0)
          const monthlyTaxCalc = monthlyTaxVal?.toFixed(2) || (0.0).toFixed(2)
          dispatch(updatePaymentStates({
            ...updatePaymentState,
            [PAYMENT_STATE.SUMMARY.AMOUNT_OWING]: total,
            [PAYMENT_STATE.SUMMARY.AMOUNT_TAX]: taxCalc,
            [PAYMENT_STATE.SUMMARY.TAX_INFO]: taxDetails,
            [PAYMENT_STATE.SUMMARY.TAX_PROVINCE]: taxProvince,
            [PAYMENT_STATE.SUMMARY.TAX_PROVINCE_ID]: taxProvinceId,
            [PAYMENT_STATE.SUMMARY.ESTIMATED_RECURRING_CHARGE]: monthlyBase,
            [PAYMENT_STATE.SUMMARY.MONTHLY_TAX_INFO]: monthlyTaxDetails,
            [PAYMENT_STATE.SUMMARY.MONTHLY_TAX]: monthlyTaxCalc
          }))
          setIsLoadingFees(false)
        }).catch(() => {
          // Tax lookup failed: fall back to the amount owing without tax.
          dispatch(updatePaymentStates({
            ...updatePaymentState,
            [PAYMENT_STATE.SUMMARY.AMOUNT_OWING]: subTotal,
            [PAYMENT_STATE.SUMMARY.AMOUNT_TAX]: (0.0).toFixed(2)
          }))
          setIsLoadingFees(false)
        })
      } else {
        // Vouchers with the 911 flag off: no fee and no tax to look up, so
        // nothing would otherwise lower the skeleton raised above.
        setIsLoadingFees(false)
      }
    }

    dispatch(updatePaymentStates({ ...updatePaymentState }))
  }, [
    addedVouchers?.length,
    promotionCodeInfo,
    multiPromotionCodeInfo?.length,
    JSON.stringify(multiPromotionCodeInfo),
    JSON.stringify(multiPromoCodes),
    selectedPlan?.id,
    selectedPhoneNumber,
    selectedPhoneNumberOption,
    selectedPaymentOption,
    provinceInfo,
    simSelection,
    simSelectionCost,
    prov,
    language,
    location?.pathname,
    serviceFeeKey,
    serviceFeeResult,
    isPromoApplying
  ])

  const hasPlan = !!selectedPlan?.id
  // Pre-selected (4-step) journeys only. Those users never see a plans step in
  // the tracker, so this link is their only route to change plan. In-flow
  // (5-step) users change plan from the plans step instead. MASC-17110 had made
  // this universal while using preSelectedPlan to pick between /plans/new and
  // /plans; that fork is gone, so the flag gates visibility again.
  const showChangePlanLink = isConsumer && hasPlan && !!preSelectedPlan
  const eSimSelected = simSelection === SIM_OPTIONS.E_SIM

  const planValueDisplay = currencyFormatter(hasPlan ? selectedPlan.value : 0, language)
  const simCostDisplay = currencyFormatter(simSelectionCost || 0, language)
  const zeroDisplay = currencyFormatter(0, language)

  const subtotalAmount = useMemo(() => (
    calculateSubTotal(selectedPlan, getPromoBundle(), simSelectionCost, serviceFee911)
  ), [
    selectedPlan,
    promotionCodeInfo,
    multiPromotionCodeInfo,
    planPromo,
    simSelectionCost,
    serviceFee911,
    enableMultiPromo
  ])
  const subtotalDisplay = currencyFormatter(subtotalAmount, language)
  // Paying by voucher deducts the voucher total from the amount owing, so Due
  // today reflects amountDue (subtotal − vouchers) rather than the full owing.
  const isVoucherPayment = selectedPaymentOption === PAYMENT_OPTIONS.VOUCHER
    && addedVouchers?.length > 0
  // Voucher surplus: vouchers exceeded the amount owing, so the leftover
  // (amountFunds) is credited to the account. Mirrors PaymentSummary.
  const showVoucherExcess = isVoucherPayment && Number(amountFunds) > 0
  const dueTodayDisplay = currencyFormatter(
    (isVoucherPayment ? amountDue : amountOwing) ?? 0,
    language
  )
  // Due monthly = recurring base (plan value, plus 911 fee when enabled) + the
  // recomputed monthly tax. estimatedRecurringCharge already folds in the 911 fee
  // once the tax-fetch effect runs, but its Redux initial value is the string
  // "0.00" (see PayAndReviewSlice). The previous `??` chain didn't fall back
  // on that because it's neither null nor undefined, so dueMonthly showed
  // $0.00 on surfaces hit before tax is computed (e.g. Plans page). Falling
  // back via `||` covers the initial-state case too.
  const recurringBaseAmount =
    Number(estimatedRecurringCharge) || Number(selectedPlan?.value ?? 0)
  const dueMonthlyDisplay = currencyFormatter(
    (recurringBaseAmount + Number(monthlyTax || 0)).toFixed(2),
    language
  )

  const taxRows = (taxInfo || []).map((tax, idx) => ({
    key: `tax-${idx}`,
    label: tax.label,
    value: currencyFormatter(tax.value, language)
  }))

  // Monthly tax types collapse into a single line, e.g. "Estimated tax (GST + QST)".
  // Each type name is parsed from its detail label ("GST (5%)" -> "GST"); the value
  // is the already-summed monthlyTax. A standalone info tooltip (same kit Tooltip as
  // the EPS CVV/postal labels) renders when Contentful supplies the copy.
  const monthlyTaxTypes = (monthlyTaxInfo || [])
    .map((tax) => (tax.label || '').split(' (')[0])
    .filter(Boolean)
  const monthlyTaxRow = monthlyTaxTypes.length > 0
    ? {
      key: 'monthly-tax',
      label: (
        <span className="flex items-center gap-1">
          {`${text.estimatedTaxLabel || ''} (${monthlyTaxTypes.join(' + ')})`.trim()}
          {text.estimatedTaxTooltip && (
            <span className="block relative top-[0.5px]">
              <Tooltip content={text.estimatedTaxTooltip} inline position="above" />
            </span>
          )}
        </span>
      ),
      value: currencyFormatter(monthlyTax || 0, language)
    }
    : null

  const serviceFeeApplicable = isServiceFee911Enabled && Number(serviceFee911) > 0

  // Normalize whichever promo source `getPromoBundle()` returns into a single
  // bundle list so rendering matches the subtotal calc.
  const activePromoSource = getPromoBundle()
  const allPromoBundles = Array.isArray(activePromoSource)
    ? activePromoSource.flatMap((promoInfo, promoIndex) => (
      (promoInfo?.bundles || []).map((bundle) => ({ ...bundle, promoIndex }))
    ))
    : (activePromoSource?.bundles || []).map((bundle) => ({ ...bundle, promoIndex: 0 }))

  const nonMonetaryPromoBundles = allPromoBundles.filter(
    (bundle) => Math.abs(Number(bundle.fee || 0)) === 0
  )

  let itemCount = 0
  if (hasPlan) itemCount += 1
  if (eSimSelected) itemCount += 1
  itemCount += nonMonetaryPromoBundles.length
  // EN "items" / FR "articles" both end in 's'; trim for the singular case.
  const itemsLabelPlural = text.itemsTitle
  const itemsLabelSingular = itemsLabelPlural?.endsWith('s')
    ? itemsLabelPlural.slice(0, -1)
    : itemsLabelPlural
  const itemsSubtitle = itemCount > 0 && itemsLabelPlural
    ? `(${itemCount} ${itemCount === 1 ? itemsLabelSingular : itemsLabelPlural})`
    : undefined

  const summaryRows = [
    {
      key: 'dueToday',
      label: text.dueTodayTitle,
      value: dueTodayDisplay,
      emphasized: true
    },
    {
      key: 'dueMonthly',
      label: text.dueMonthlyTitle,
      value: dueMonthlyDisplay,
      emphasized: true
    }
  ]


  const monetaryPromoRows = allPromoBundles
    .filter((bundle) => Math.abs(Number(bundle.fee || 0)) > 0)
    .map((bundle) => {
      const discountAbs = Math.abs(Number(bundle.fee))
      return {
        key: `promo-bundle-${bundle.promoIndex}-${bundle.id}`,
        label: resolveBundleName(bundle, language),
        value: `- ${currencyFormatter(discountAbs, language)}`,
        credit: true
      }
    })

  const serviceFee911Row = serviceFeeApplicable
    ? {
      key: 'serviceFee911',
      label: (
        <span className="flex items-center gap-1">
          {serviceFeeName}
          {text.serviceFeeCollected && (
            <span className="block relative top-[0.5px]">
              <Tooltip content={text.serviceFeeCollected} inline position="above" />
            </span>
          )}
        </span>
      ),
      value: currencyFormatter(serviceFee911, language)
    }
    : null

  const voucherExcessRow = showVoucherExcess
    ? {
      key: 'voucherExcess',
      label: (
        <span>
          <span className="font-h4">{text.voucherExcess}</span>
          {text.fundsAddedToTheAccount ? ` ${text.fundsAddedToTheAccount}` : ''}
        </span>
      ),
      value: `+ ${currencyFormatter(amountFunds, language)}`,
      className: 'my-6 rounded border border-dashed border-grey p-2'
    }
    : null

  const firstPaymentRows = [
    ...monetaryPromoRows,
    ...(serviceFee911Row ? [serviceFee911Row] : []),
    { key: 'subtotal', label: text.subTotalTitle, value: subtotalDisplay },
    ...(isVoucherPayment
      ? [{
        key: 'vouchers',
        label: text.vouchersTitle,
        value: `- ${currencyFormatter(amountTotalVoucher ?? 0, language)}`,
        credit: true
      }]
      : []),
    ...taxRows,
    {
      key: 'dueToday',
      label: text.dueTodayTitle,
      value: dueTodayDisplay,
      emphasized: true
    },
    ...(voucherExcessRow ? [voucherExcessRow] : [])
  ]

  const monthlyPaymentRows = [
    { key: 'subtotal', label: text.subTotalTitle, value: planValueDisplay },
    ...(monthlyTaxRow ? [monthlyTaxRow] : []),
    ...(serviceFee911Row ? [{ ...serviceFee911Row, key: 'serviceFee911-monthly' }] : []),
    {
      key: 'dueMonthly',
      label: text.dueMonthlyTitle,
      value: dueMonthlyDisplay,
      emphasized: true
    }
  ]

  const paymentSections = [
    {
      key: 'firstPayment',
      title: text.firstPaymentTitle,
      rows: firstPaymentRows
    },
    {
      key: 'monthlyPayment',
      title: text.monthlyPaymentTitle,
      rows: monthlyPaymentRows
    }
  ]

  // Construct during render — these path classes call `useLocation()` in
  // their constructor chain, so `new ConsumerActivationPaths()` inside an
  // event handler triggers "Invalid hook call".
  const consumerActivationPaths = new ConsumerActivationPaths()

  // Always the plans page: in the new designs it is the single plan-selection
  // surface for both the pre-selected (4-step) and in-flow (5-step) journeys, so
  // "change plan" no longer forks to the legacy /plans/new.
  const handleChangePlanClick = () => {
    history.push(consumerActivationPaths.Plans, { from: location.pathname })
  }

  const planLogo = text.planLogoUrl ? (
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Image
        src={text.planLogoUrl}
        alt={text.planLogoAlt}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </div>
  ) : undefined

  const planItem = hasPlan ? (
    <Cart.Item
      image={planLogo}
      title={toSafeText(selectedPlan.name)}
      action={
        showChangePlanLink ? (
          // Kit `Link` styled as a button — onClick triggers an in-app
          // navigation via history.push, so no static `href` to pass.
          // Matches how kit `Link` is wired elsewhere in the app (IntroPage,
          // DealerSessionCard) for consistency.
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <Link onClick={handleChangePlanClick}>{text.changePlanLinkTitle || (language === 'fr' ? 'Changer de forfait' : 'Change Plan')}</Link>
        ) : null
      }
      value={planValueDisplay}
    >
      {selectedPlan.features?.length > 0 && (
        // `list-disc list-outside` re-enables bullets (Tailwind preflight
        // zeros `list-style`). `text-black` pins marker + text color since
        // the surrounding kit Cart breaks the inheritance chain.
        <ul
          className="list-disc list-outside text-black"
          style={{ margin: '4px 0 0', paddingLeft: 20 }}
        >
          {selectedPlan.features.map((feature) => (
            <li key={feature}>
              <Typography variant={{ size: 'small' }}>{renderFeatureText(feature)}</Typography>
            </li>
          ))}
        </ul>
      )}
    </Cart.Item>
  ) : (
    <Cart.Item
      image={planLogo}
      title={text.noPlanSelectedTitle}
      value={zeroDisplay}
    />
  )

  const simItem = eSimSelected ? (
    <Cart.Item
      className="kpre-order-summary-sim-item"
      title={simSelection}
      subtitle={text.oneTimePaymentTitle || undefined}
      value={simCostDisplay}
      testId="order-summary-sim"
    />
  ) : null

  const nonMonetaryPromoItems = nonMonetaryPromoBundles.map((bundle) => (
    <Cart.Item
      key={`promo-item-${bundle.promoIndex}-${bundle.id}`}
      className="kpre-order-summary-promo-item"
      title={resolveBundleName(bundle, language)}
      value={currencyFormatter(0, language)}
      testId={`order-summary-promo-${bundle.id}`}
    />
  ))

  const cartItems = (
    <>
      {planItem}
      {simItem}
      {nonMonetaryPromoItems}
    </>
  )

  if (isLoading || isLoadingFees) {
    return <Cart.Skeleton />
  }

  return (
    <Cart
      title={text.orderSummaryTitle}
      subtitle={itemsSubtitle}
      showDetailsLabel={text.showTitle}
      hideDetailsLabel={text.hideTitle}
      items={cartItems}
      collapsedContent={summaryRows.map((row, index) => (
        <React.Fragment key={row.key}>
          <Cart.Row
            label={row.label}
            value={row.value}
            emphasized={row.emphasized}
            credit={row.credit}
          />
          {index < summaryRows.length - 1 && <Divider />}
        </React.Fragment>
      ))}
      expandedContent={paymentSections.map((section, sectionIndex) => (
        <React.Fragment key={section.key}>
          <Cart.Section title={section.title}>
            {section.rows.map((row) => (
              <Cart.Row
                key={row.key}
                label={row.label}
                value={row.value}
                emphasized={row.emphasized}
                credit={row.credit}
                className={row.className}
              />
            ))}
          </Cart.Section>
          {sectionIndex < paymentSections.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    />
  )
}

export default OrderSummary
